import { useBasket, useCategory, useNavigation, useProduct } from "@utilities/hooks";
import { Category } from "@utilities/types";
import { useCallback, useEffect, useRef, useState } from "react";

export const useControllers = () => {

    const { navigateTo } = useNavigation();
    const {
        categories,
        onGetAllOrganisationCategories,
    } = useCategory();
    const {
        products, productsPagination,
        onGetAllOrganisationProducts,
        onGetAllOrganisationProductsByCategory,
        onSearchProducts, searchProductsLoading,
    } = useProduct();
    const {
        activeBasket, getCollabBasketError, getCollabBasketLoading,
        baskets, saveBasketError, saveBasketLoading,
        deleteBasketError, deleteBasketLoading, totalSavings,
        onAddProductInBasket, onRemoveProductInBasket,
        onGetAllCollabBasket, onDeleteBasket, setActiveBasket,
    } = useBasket();

    const [basketListModal, setBasketListModal] = useState<boolean>(false);
    const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>('Tout');
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        onGetAllOrganisationCategories();
        onGetAllOrganisationProducts();
    }, []);

    useEffect(() => {
        onGetAllCollabBasket();
    }, []);

    const handleDrawerToggle = useCallback(() => setIsDrawerOpen((prev) => !prev), []);
    const handleDrawerClose = useCallback(() => setIsDrawerOpen(false), []);
    const toggleCart = useCallback(() => setIsCartOpen(!isCartOpen), [isCartOpen]);

    const handleGetProductsByCategory = useCallback(async (categoryId?: string) => {
        // Reset search when changing category
        setSearchTerm('');
        setIsSearching(false);
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (categoryId) {
            await onGetAllOrganisationProductsByCategory(categoryId);
            const category = categories.find((cat) => cat._id === categoryId);
            if (category) {
                setSelectedCategoryName(category.name);
                setSelectedCategory(category);
            }
        } else {
            await onGetAllOrganisationProducts();
            setSelectedCategoryName('Tout');
            setSelectedCategory(null);
        }
    }, [categories, onGetAllOrganisationProductsByCategory, onGetAllOrganisationProducts]);

    const handleChangePage = useCallback(async (page: number) => {
        if (isSearching && searchTerm.trim()) {
            await onSearchProducts(searchTerm, page);
        } else if (selectedCategory) {
            await onGetAllOrganisationProductsByCategory(selectedCategory._id, page);
        } else {
            await onGetAllOrganisationProducts(page);
        }
    }, [selectedCategory, onGetAllOrganisationProducts, onGetAllOrganisationProductsByCategory, onSearchProducts, isSearching, searchTerm]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchTerm(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (value.trim()) {
            setIsSearching(true);
            // Set a new timeout for search
            searchTimeoutRef.current = setTimeout(() => {
                onSearchProducts(value.trim());
            }, 500); // Délai de 500ms pour éviter trop d'appels
        } else {
            setIsSearching(false);
            // Si le champ de recherche est vide, retourner aux produits par défaut
            if (selectedCategory) {
                onGetAllOrganisationProductsByCategory(selectedCategory._id);
            } else {
                onGetAllOrganisationProducts();
            }
        }
    }, [onSearchProducts, onGetAllOrganisationProducts, onGetAllOrganisationProductsByCategory, selectedCategory]);

    const handleCancelSearch = useCallback(() => {
        setSearchTerm('');
        setIsSearching(false);

        // Clear timeout if exists
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Retourner aux produits par défaut
        if (selectedCategory) {
            onGetAllOrganisationProductsByCategory(selectedCategory._id);
            setSelectedCategoryName(selectedCategory.name);
        } else {
            onGetAllOrganisationProducts();
            setSelectedCategoryName('Tout');
        }
    }, [onGetAllOrganisationProducts, onGetAllOrganisationProductsByCategory, selectedCategory]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return {
        isCartOpen, isDrawerOpen, productsPagination,
        categories, products, selectedCategoryName,
        basketListModal, baskets, activeBasket, totalSavings,
        searchTerm, isSearching, searchProductsLoading,
        setBasketListModal, toggleCart,
        handleDrawerToggle, setActiveBasket,
        handleDrawerClose, onDeleteBasket,
        onAddProductInBasket, handleChangePage,
        onRemoveProductInBasket, handleSearchChange,
        handleGetProductsByCategory, handleCancelSearch,
    };
};
